import { IIntegratedCare, IStock } from '@alberta/konexi-shared';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NavParams, PopoverController } from '@ionic/angular';
import { Observable } from 'rxjs';
import { SingleOrderViewModel } from 'src/app/business/single-order/single-order-view-model';
import { IntegratedCareDB } from 'src/app/common/repository/databases';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { TenantConfigService } from 'src/app/shared/services/tenant-config/tenant-config.service';

import { OrderArticledataPopoverComponent } from '../../../careproposal/order-edit/order-articledata/order-articledata-popover/order-articledata-popover.component';
import { IntegratedCareStockService } from '../../integratedcare-detail/integrated-care-stock/integrated-care-stock.service';

@Component({
  selector: 'itl-single-order-articledata',
  styleUrls: ['./single-order-articledata.component.scss'],
  templateUrl: './single-order-articledata.component.html',
  providers: [IntegratedCareStockService],
})
export class SingleOrderArticledataComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public order: SingleOrderViewModel;
  @Input() public isNew: boolean;
  @Input() public form: UntypedFormGroup;
  @Input() public templateRef: ViewContainerRef;
  @Output() public templateRefChange = new EventEmitter<ViewContainerRef>();
  @ViewChild('footerBar', { static: true }) private template: ViewContainerRef;

  public allChecked = false;
  public nothingChecked = false;
  public changedItemIndex;
  public configStockEnabled = false;
  public stock$: Observable<{ [key: string]: IStock }>;
  public isArticleEditDisabled = false;
  private _integratedCare: IIntegratedCare;

  constructor(
    private _popOverController: PopoverController,
    private _queryService: QueryService,
    private _integratedCareStockService: IntegratedCareStockService,
    private _tenantConfigService: TenantConfigService,
    private _navParams: NavParams
  ) {}

  async ngOnInit(): Promise<void> {
    this.isArticleEditDisabled = this._navParams.get('disableArticleEdit');
    this._integratedCare = await this._queryService.get<IIntegratedCare>(this.order.integratedCareId, IntegratedCareDB);
    this.setAllOrNothingChecked();

    this.configStockEnabled = await this._tenantConfigService.canQueryStock();

    if (this.configStockEnabled) {
      this._integratedCareStockService.init();
      this.stock$ = this._integratedCareStockService.stock();
    }
  }

  ngAfterViewInit() {
    this.templateRefChange.emit(this.template);
  }

  ngOnDestroy() {
    this.templateRefChange.emit(null);
  }

  async changed(item) {
    const index = this.order.orderedArticleLines.findIndex(element => {
      return element.articleId === item.articleId;
    });
    if (index === -1) {
      return;
    }
    if (
      this.order.orderedArticleLines[index].batchNumber !== item.batchNumber ||
      this.order.orderedArticleLines[index].serialNumber !== item.serialNumber
    ) {
      this.changedItemIndex = index;
    }
    this.order.orderedArticleLines[index] = item;
    this.setAllOrNothingChecked();
    await this.order.initMaxAmount(this._integratedCare, this._queryService);
    this.updateForm();
  }

  setAllRecommendations() {
    this.order.orderedArticleLines.forEach(articleLine => {
      articleLine.quantity = articleLine.recommendedQuantity;
    });
    this.updateForm();
  }

  allCheckedToggled() {
    this.order.orderedArticleLines.forEach(articleLine => {
      articleLine.active = !this.allChecked;
    });
  }

  setAllOrNothingChecked() {
    this.nothingChecked = !this.order.orderedArticleLines.some(articleLine => articleLine.active === true);
    this.allChecked = this.order.orderedArticleLines.every(articleLine => articleLine.active);
  }

  updateForm() {
    this.form.controls['orderedArticleLines'].setValue(this.order.orderedArticleLines);
    this.form.controls['orderedArticleLines'].markAsTouched();
  }

  async presentPopover() {
    const popover = await this._popOverController.create({
      component: OrderArticledataPopoverComponent,
      cssClass: ['ordereditpopover', 'fullPageModal'],
      componentProps: {
        allChecked: this.allChecked,
      },
    });

    popover.onDidDismiss().then(async retVal => {
      if (retVal.data) {
        if (retVal.data.allCheckedToggled) {
          this.allCheckedToggled();
          this.setAllOrNothingChecked();
          await this.order.initMaxAmount(this._integratedCare, this._queryService);
        }
        if (retVal.data.setRecommendation) {
          this.setAllRecommendations();
          await this.order.initMaxAmount(this._integratedCare, this._queryService);
        }
      }
      this.updateForm();
    });

    await popover.present();
  }

  public async checkStock() {
    const customerNumbers = this.order.orderedArticleLines
      .filter(
        extendedArticle =>
          extendedArticle.article.customerNumber !== null || extendedArticle.article.customerNumber !== undefined
      )
      .map(extendedArticle => extendedArticle.article.customerNumber);
    if (customerNumbers.length > 0) {
      await this._integratedCareStockService.checkStock(customerNumbers, this.order._id);
    }
  }
}
